import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, UserRound, Wand2, X, Film } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { UploadZone } from "./UploadZone";
import { StylePicker } from "./StylePicker";
import { StudioResult } from "./StudioResult";
import { api, API, errorText } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { scrollToId } from "@/lib/scroll";

const MAX = 15 * 1024 * 1024;
const field = "w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#d5d9df]/70 resize-none";
const MODES = [
  { id: "background", label: "Background Swap", short: "Background", icon: ImageIcon },
  { id: "person", label: "Person Swap", short: "Person", icon: UserRound },
  { id: "generate", label: "Image Generator", short: "Generate", icon: Wand2 },
  { id: "video", label: "Video Swap", short: "Video", icon: Film },
];
const VIDEO_COST = 10;
const MAX_VIDEO = 50 * 1024 * 1024;

const checkVideo = (f) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => {
    URL.revokeObjectURL(v.src);
    if (v.duration < 3 || v.duration > 10.05) resolve("Videos must be 3 to 10 seconds long.");
    else if (Math.min(v.videoWidth, v.videoHeight) < 720) resolve("Videos must be at least 720p.");
    else resolve(null);
  };
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(f);
});

const VideoZone = ({ file, preview, onFile }) => (
  <label data-testid="video-input-zone" className="relative block cursor-pointer rounded-2xl border-2 border-dashed border-white/15 hover:border-white/40 bg-white/[0.02] overflow-hidden transition-[border-color]">
    <input type="file" accept="video/mp4,video/quicktime" className="hidden" data-testid="video-file-input" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
    {preview ? (
      <video src={preview} muted loop autoPlay playsInline className="h-64 w-full object-cover" data-testid="video-preview" />
    ) : (
      <div className="h-64 flex flex-col items-center justify-center gap-3 text-center px-6">
        <Film className="h-8 w-8 text-slate-300" />
        <div className="font-display font-semibold">Drop your clip here</div>
        <div className="text-sm text-slate-400">MP4 or MOV · 3-10 seconds · 720p or higher · up to 50MB</div>
      </div>
    )}
    {file && <span className="absolute bottom-3 left-3 pill-label">{file.name}</span>}
  </label>
);
const ASPECTS = [
  { id: "landscape", label: "16:9" },
  { id: "portrait", label: "9:16" },
  { id: "square", label: "1:1" },
];
const PLACEHOLDER = {
  background: "e.g. golden hour light, wet road reflections",
  person: "e.g. a woman in her 30s with short black hair and a leather jacket",
  generate: "e.g. an empty mountain road at blue hour winding through snowy peaks",
  video: "e.g. pink sunset sky, snowy mountains in the distance",
};

const usePreview = (file) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
};

const Step = ({ n, title, children }) => (
  <div className="mb-9">
    <div className="flex items-center gap-3 mb-4">
      <span className="h-7 w-7 rounded-md bg-[#3b4049] grid place-items-center text-xs font-display font-bold">{n}</span>
      <h3 className="font-display font-semibold">{title}</h3>
    </div>
    {children}
  </div>
);

const ModeTabs = ({ mode, onChange }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 p-1.5 rounded-xl panel mb-10 max-w-2xl" data-testid="studio-mode-tabs">
    {MODES.map((m) => (
      <button key={m.id} data-testid={`studio-mode-${m.id}`} onClick={() => onChange(m.id)}
        className={`flex items-center justify-center gap-2 py-3 rounded-lg text-xs sm:text-sm font-medium transition-[background-color,color] ${mode === m.id ? "bg-[#3b4049] text-white shadow-[0_0_24px_rgba(190,197,208,0.6)]" : "text-slate-300 hover:text-white"}`}>
        <m.icon className="h-4 w-4 shrink-0" /> <span className="sm:hidden">{m.short}</span><span className="hidden sm:inline">{m.label}</span>
      </button>
    ))}
  </div>
);

const BackgroundPick = ({ style, setStyle, bg, bgPreview, onBg, clearBg }) => (
  <>
    {bg ? (
      <div className="relative rounded-2xl overflow-hidden ring-2 ring-[#d5d9df] h-40" data-testid="studio-own-background">
        <img src={bgPreview} alt="Your background" className="h-full w-full object-cover" />
        <span className="absolute top-3 left-3 pill-label">Your background</span>
        <button data-testid="studio-clear-background" onClick={clearBg} className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full bg-black/60 hover:bg-black/80" aria-label="Remove background">
          <X className="h-4 w-4" />
        </button>
      </div>
    ) : (
      <>
        <StylePicker value={style} onChange={setStyle} />
        <div className="text-xs text-slate-400 my-4 text-center">or use your own background</div>
        <UploadZone file={bg} preview={bgPreview} onFile={onBg} testId="background" title="Upload a background photo" compact />
      </>
    )}
  </>
);

export const Studio = () => {
  const { user, openAuth, refresh } = useAuth();
  const [mode, setMode] = useState("background");
  const [file, setFile] = useState(null);
  const [clip, setClip] = useState(null);
  const clipPreview = usePreview(clip);
  const [character, setCharacter] = useState(null);
  const [bg, setBg] = useState(null);
  const [style, setStyle] = useState("sunset");
  const [aspect, setAspect] = useState("landscape");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const preview = usePreview(file);
  const charPreview = usePreview(character);
  const bgPreview = usePreview(bg);

  const switchMode = (m) => {
    setMode(m);
    setResult(null);
  };

  useEffect(() => {
    const onMode = (e) => {
      if (MODES.some((m) => m.id === e.detail)) switchMode(e.detail);
    };
    window.addEventListener("fs:mode", onMode);
    return () => window.removeEventListener("fs:mode", onMode);
  }, []);

  const pickClip = async (f) => {
    if (!f.type.startsWith("video/")) return toast.error("Please choose an MP4 or MOV video.");
    if (f.size > MAX_VIDEO) return toast.error("Video must be under 50MB.");
    const problem = await checkVideo(f);
    if (problem) return toast.error(problem);
    setClip(f);
    setResult(null);
  };

  const pick = (setter) => (f) => {
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (f.size > MAX) return toast.error("Image must be under 15MB.");
    setter(f);
    setResult(null);
  };

  const validate = () => {
    if (mode === "video") return clip ? (style === "custom" && !prompt.trim() ? "Describe the world you want behind your clip." : null) : "Upload your video clip first.";
    if (mode === "generate") return prompt.trim().length >= 3 ? null : "Describe the scene you want to create.";
    if (!file) return "Upload your photo first.";
    if (mode === "person" && !character && !prompt.trim()) return "Add a photo of the new person or describe them.";
    if (mode === "background" && style === "custom" && !bg && !prompt.trim()) return "Describe your background idea or upload one.";
    return null;
  };

  const run = async () => {
    const problem = validate();
    if (problem) return toast.error(problem);
    if (!user) return openAuth("register", "Create a free account to use the Studio. Your photos stay right here.");
    const cost = mode === "video" ? VIDEO_COST : 1;
    if (!user.unlimited && (user.free_left ?? 0) < 1 && (user.credits ?? 0) < cost) {
      toast.error(cost > 1 ? `Videos use ${cost} credits. Pick a plan or pack to get more.` : "You're out of credits. Pick a plan or pack to keep creating.");
      return scrollToId("pricing");
    }
    setBusy(true);
    setResult(null);
    const fd = new FormData();
    fd.append("prompt", prompt);
    fd.append("origin_url", window.location.origin);
    if (mode === "video") {
      fd.append("video", clip);
      fd.append("mode", "background");
      fd.append("style", style);
    } else if (mode === "generate") {
      fd.append("aspect", aspect);
      if (file) fd.append("source", file);
    } else {
      fd.append("photo", file);
      fd.append("mode", mode);
      fd.append("style", mode === "background" && !bg ? style : "custom");
      if (mode === "person" && character) fd.append("character", character);
      if (mode === "background" && bg) fd.append("background", bg);
    }
    try {
      if (mode === "video") {
        const { data: sub } = await api.post("/studio/video", fd, { timeout: 180000 });
        refresh();
        toast.success("Video uploaded. Rendering usually takes a few minutes.");
        for (let i = 0; i < 240; i += 1) {
          await new Promise((r) => setTimeout(r, 5000));
          const { data: o } = await api.get(`/my/orders/${sub?.order_id}`);
          if (o?.has_result) {
            setResult({ id: sub.order_id, mode: "video" });
            toast.success("Your new video is ready");
            return;
          }
          if (o?.ai_status === "failed") throw new Error(o?.ai_error || "The video failed. Your credits were refunded.");
        }
        toast("Still rendering. Check My Orders in a few minutes.");
        return;
      }
      const { data } = await api.post(mode === "generate" ? "/studio/generate" : "/studio/swap", fd, { timeout: 180000 });
      setResult({ id: data?.order_id, mode, before: preview });
      toast.success(mode === "generate" ? "Your new scene is ready" : "Your new image is ready");
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const useAsBackground = async () => {
    try {
      const res = await fetch(`${API}/my/orders/${result.id}/result`, { credentials: "include" });
      const blob = await res.blob();
      setBg(new File([blob], "generated-background.png", { type: blob.type || "image/png" }));
      setFile(null);
      setPrompt("");
      switchMode("background");
      toast.success("Background set. Now upload the photo you want to place in it.");
    } catch {
      toast.error("Couldn't load that image. Please try again.");
    }
  };

  return (
    <section id="order" data-testid="studio-section" className="relative py-20 lg:py-28 bg-[#08080a] border-y border-white/5 overflow-hidden">
      <div className="absolute -left-40 top-20 h-[500px] w-[500px] rounded-full bg-[#3b4049]/20 blur-[150px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8">
        <SectionHead eyebrow="The Studio" title={<>Upload. Swap.<br />Done.</>}>
          Swap the background, swap the person, or make a brand-new scene. Photos cost 1 credit and land here in seconds. Video swaps cost 10 credits and render in a few minutes.
        </SectionHead>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-7">
            <ModeTabs mode={mode} onChange={switchMode} />
            {mode === "video" ? (
              <>
                <Step n="1" title="Upload your clip"><VideoZone file={clip} preview={clipPreview} onFile={pickClip} /></Step>
                <Step n="2" title="Choose a world">
                  <StylePicker value={style} onChange={setStyle} />
                </Step>
                <Step n="3" title={style === "custom" ? "Describe your world" : "Extra details (optional)"}>
                  <textarea data-testid="studio-prompt-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} rows={3} placeholder={PLACEHOLDER.video} className={field} />
                </Step>
              </>
            ) : mode === "generate" ? (
              <>
                <Step n="1" title="Describe your scene">
                  <textarea data-testid="studio-prompt-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} rows={4} placeholder={PLACEHOLDER.generate} className={field} />
                  <div className="flex gap-2 mt-3" data-testid="studio-aspect-picker">
                    {ASPECTS.map((a) => (
                      <button key={a.id} data-testid={`studio-aspect-${a.id}`} onClick={() => setAspect(a.id)}
                        className={`px-4 py-2 rounded-lg text-sm border transition-[background-color,border-color] ${aspect === a.id ? "bg-[#3b4049] border-[#3b4049]" : "border-white/15 text-slate-300 hover:border-[#d5d9df]/60"}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </Step>
                <Step n="2" title="Start from a photo (optional)">
                  <UploadZone file={file} preview={preview} onFile={pick(setFile)} title="Upload a photo to edit" compact />
                </Step>
              </>
            ) : (
              <>
                <Step n="1" title="Upload your photo"><UploadZone file={file} preview={preview} onFile={pick(setFile)} /></Step>
                {mode === "background" ? (
                  <Step n="2" title="Choose a world">
                    <BackgroundPick style={style} setStyle={setStyle} bg={bg} bgPreview={bgPreview} onBg={pick(setBg)} clearBg={() => setBg(null)} />
                  </Step>
                ) : (
                  <Step n="2" title="Who takes their spot? (photo optional)">
                    <UploadZone file={character} preview={charPreview} onFile={pick(setCharacter)} testId="character" title="Drop a photo of the new person" />
                  </Step>
                )}
                <Step n="3" title={mode === "person" ? "Describe the new person (optional with a photo)" : "Extra details (optional)"}>
                  <textarea data-testid="studio-prompt-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} maxLength={1000} rows={3} placeholder={PLACEHOLDER[mode]} className={field} />
                </Step>
              </>
            )}
          </Reveal>
          <div className="lg:col-span-5">
            <StudioResult mode={mode} user={user} preview={mode === "video" ? null : preview} clipPreview={clipPreview} result={result} busy={busy} onRun={run} onUseAsBackground={useAsBackground} />
          </div>
        </div>
      </div>
    </section>
  );
};
